import React, { Fragment, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { Button, useDataProvider, useUnselectAll } from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import TableResultMergePurchaseInvoicePdf from "./TableResultMergePurchaseInvoicePdf";
import { mergePurchaseInvoicePdfs } from "../tools";

interface IBulkMergePdfProps {
  filteredData: ISubcontractorPurchaseInvoice[];
  basePath: string;
  filterValues: any;
  label: string;
}

interface IMergePdfResults {
  id: number;
  description: string;
  status: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const BulkActionMergePdfButton = ({ filteredData }: IBulkMergePdfProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IMergePdfResults[]>([]);
  const [processing, setProcessing] = useState(false);

  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  // Filter and mapped list of purchase invoices that have a valid PDF URL
  const selectedPurchaseInvoices = filteredData
    .filter(
      (data) =>
        data.pdfUrl && typeof data.pdfUrl === "string" && data.pdfUrl.trim(),
    )
    .map((data) => ({
      id: data.id,
      description: data.description,
      status: "Loading...", // Initial status before processing
    }));

  // Filter and mapped list of purchase invoices that are missing or have invalid PDF URLs
  const selectedPurchaseInvoicesWithNullPdf = filteredData
    .filter(
      (data) =>
        !data.pdfUrl || typeof data.pdfUrl !== "string" || !data.pdfUrl.trim(),
    ) // Filter only for null PDF URL
    .map((data) => ({
      id: data.id,
      description: data.description,
      status: "PDF link is empty or invalid",
    }));

  // Get only the IDs of valid purchase invoices for confirmation dialog
  const validPurchaseInvoicePdfUrlIds = selectedPurchaseInvoices.map(
    (data) => data.id,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorPurchaseInvoices");
      setResults([]);
      setFinished(false);
    }

    setOpen(false);
  };

  // Handles the merge process
  const handleMerge = async (selectedPurchaseInvoices: IMergePdfResults[]) => {
    setProcessing(true);

    await mergePurchaseInvoicePdfs({
      selectedPurchaseInvoices,
      provider,
      updateResults: setResults,
      delayMs: 500,
      revokeDelayMs: 60000, // revoke after 60 seconds
    });

    setFinished(true);
    setProcessing(false);
  };

  return (
    <Fragment>
      <Button
        label="Merge Selected"
        onClick={handleOpen}
        style={{ backgroundColor: "#1976d2" }}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Merge Selected Purchase Invoice PDF
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultMergePurchaseInvoicePdf records={results} />
          ) : (
            <>
              <DialogContentText color="initial">
                Are you sure you want to merge this selected purchase invoice
                pdf with ID ({validPurchaseInvoicePdfUrlIds.toString()})?
              </DialogContentText>
              {selectedPurchaseInvoicesWithNullPdf.length > 0 && (
                <>
                  <DialogContentText color="initial">
                    You are about to merge{" "}
                    <strong>
                      {selectedPurchaseInvoices.length} out of{" "}
                      {filteredData.length}
                    </strong>{" "}
                    PDF.
                    <br />
                    Below is the list of purchase invoice item that don’t have
                    PDF link.
                  </DialogContentText>
                  <TableResultMergePurchaseInvoicePdf
                    records={selectedPurchaseInvoicesWithNullPdf}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={() => handleMerge(selectedPurchaseInvoices)}
              color="primary"
              label="Confirm"
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default BulkActionMergePdfButton;
